import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AppSettingsView,
  ModuleKey,
  ModulesView,
  SaveModulesInput,
  CustomFieldModule,
  CustomFieldValueView,
  CustomFieldView,
  FxRateInput,
  FxRateView,
  MetricDictionaryView,
  PublishDictionaryInput,
  SaveCustomValuesInput,
  SaveOrganisationSettingsInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPost, apiPut } from '../../../lib/api-client';
import { APP_SETTINGS_KEY, applyAppSettings } from '../../../lib/app-settings';
import { fetchSession, SESSION_QUERY_KEY } from '../../auth/api/auth-api';

// S31 — ADMIN-15 settings, the FX table, ADMIN-14 and the custom fields on a record. ADMIN-08 sources, ADMIN-13
// custom fields and name matching have their own files since the Admin redesign (batch 2). Edits apply on screen first and roll back if the server refuses (§5a rule 1).

const HTTP_CONFLICT = 409;
const FX_KEY = ['admin', 'fx-rates'] as const;
const DICTIONARY_KEY = ['metric-dictionary'] as const;
const fieldsKey = (module: CustomFieldModule): readonly string[] => ['custom-fields', module];
const valuesKey = (module: CustomFieldModule, entityId: string): readonly string[] => ['custom-field-values', module, entityId];

// --- ADMIN-15 ------------------------------------------------------------------------------

export function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation<AppSettingsView, ApiError, SaveOrganisationSettingsInput>({
    mutationFn: (input) => apiPut<AppSettingsView>('/admin/settings', input),
    onSuccess: async (view) => {
      applyAppSettings(view);
      queryClient.setQueryData(APP_SETTINGS_KEY, view);
      // Every visible date, number and amount re-renders in the new format.
      await queryClient.invalidateQueries();
    },
    onError: async (error) => {
      // Refused as stale: someone else saved. Fetch their version so the page can say so and offer it.
      if (error.status === HTTP_CONFLICT) await queryClient.invalidateQueries({ queryKey: APP_SETTINGS_KEY });
    },
  });
}

// --- R1: ADMIN-17 Master settings (§L28) ------------------------------------------------------

/**
 * Switch modules on and off. Applied to the shell at once — menus, tabs and the palette close up
 * before the server answers (§5a rule 1) — and rolled back if it refuses. The server pushes a hint
 * to every other browser, which refetches its settings.
 */
const SAVE_MODULES_KEY = ['save-modules'] as const;
/** ADMIN-17's own read: the switches with who last changed each (`GET /admin/modules`). */
export const MODULES_KEY = ['admin', 'modules'] as const;

export type ModuleChange = SaveModulesInput['changes'][number];

/** "Last changed" per part. The switches themselves are read from the app settings, which every screen shares. */
export function useModulesView(): UseQueryResult<ModulesView, ApiError> {
  return useQuery<ModulesView, ApiError>({
    queryKey: MODULES_KEY,
    queryFn: () => apiGet<ModulesView>('/admin/modules'),
    placeholderData: (previous) => previous,
  });
}

/** The cache's switches with these changes on top. */
function withChanges(view: AppSettingsView, changes: readonly ModuleChange[]): AppSettingsView {
  const modules: Record<ModuleKey, boolean> = { ...view.modules };
  for (const change of changes) modules[change.module] = change.enabled;
  return { ...view, modules };
}

/**
 * ADMIN-17's save. It sends only the modules this change moves — never a whole state — so an Undo, or a second switch
 * made while the first is saving, can't put back a switch flipped in between. Saves queue behind each other (one
 * scope) and each reads the settings version when it actually sends, not when it was clicked, so a quick second
 * switch is never refused as stale (§5a rule 1: the click never waits).
 */
export function useSaveModules() {
  const queryClient = useQueryClient();
  return useMutation<ModulesView, ApiError, { readonly changes: readonly ModuleChange[] }, { readonly before: readonly ModuleChange[] }>({
    mutationKey: SAVE_MODULES_KEY,
    scope: { id: 'admin-modules' },
    mutationFn: ({ changes }) => {
      const version = queryClient.getQueryData<AppSettingsView>(APP_SETTINGS_KEY)?.modulesVersion ?? 0;
      return apiPut<ModulesView>('/admin/modules', { version, changes });
    },
    onMutate: async ({ changes }) => {
      await queryClient.cancelQueries({ queryKey: APP_SETTINGS_KEY });
      const current = queryClient.getQueryData<AppSettingsView>(APP_SETTINGS_KEY);
      if (!current) return { before: [] };
      queryClient.setQueryData<AppSettingsView>(APP_SETTINGS_KEY, withChanges(current, changes));
      return { before: changes.map((change) => ({ module: change.module, enabled: current.modules[change.module] })) };
    },
    onError: async (error, _input, context) => {
      // Put back this change's switches only: a later switch still saving keeps its own.
      const current = queryClient.getQueryData<AppSettingsView>(APP_SETTINGS_KEY);
      if (current && context) queryClient.setQueryData<AppSettingsView>(APP_SETTINGS_KEY, withChanges(current, context.before));
      // Stale: someone else switched something. Read the switches again (§5a rule 1: roll back, then reconcile).
      if (error.status === HTTP_CONFLICT) await Promise.all([queryClient.invalidateQueries({ queryKey: APP_SETTINGS_KEY }), queryClient.invalidateQueries({ queryKey: MODULES_KEY })]);
    },
    onSuccess: (view) => {
      queryClient.setQueryData(MODULES_KEY, view);
      const current = queryClient.getQueryData<AppSettingsView>(APP_SETTINGS_KEY);
      if (!current) return;
      // A save still queued has already drawn its own switch; the server's answer to this one
      // would draw it back for a moment (§5a rule 3), so only the version is taken until the last.
      const isLast = queryClient.isMutating({ mutationKey: SAVE_MODULES_KEY }) <= 1;
      queryClient.setQueryData<AppSettingsView>(APP_SETTINGS_KEY, { ...current, modulesVersion: view.version, ...(isLast ? { modules: view.modules } : {}) });
    },
  });
}

export type VentureLogoChange =
  | { readonly kind: 'upload'; readonly ventureId: string; readonly file: File }
  | { readonly kind: 'remove'; readonly ventureId: string };

/**
 * A venture's own logo (DECISIONS-LOG 2026-10-06). Each logo's version rides on the session's
 * venture list, so the session is what is refetched — the switcher and this card both read it.
 */
export function useChangeVentureLogo() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, VentureLogoChange>({
    mutationFn: (change) => {
      const path = `/organisation/ventures/${change.ventureId}/logo`;
      if (change.kind === 'remove') return apiDelete<void>(path);
      const form = new FormData();
      form.append('logo', change.file);
      return apiPut<void>(path, form);
    },
    // Fetched, not invalidated: the session query never goes stale and nothing observes it,
    // so an invalidation would mark it and change nothing on screen. Awaited, so the button
    // stays pending until the new logo is there to see (§5a rule 3).
    onSuccess: async () => {
      await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 0 });
    },
  });
}

// --- The FX table (D13) --------------------------------------------------------------------

/** `isEnabled`: false for someone who may not open the page, so nothing is asked for and refused (review W15). */
export function useFxRates(isEnabled = true): UseQueryResult<readonly FxRateView[], ApiError> {
  const queryClient = useQueryClient();
  return useQuery<readonly FxRateView[], ApiError>({
    queryKey: FX_KEY,
    queryFn: async () => {
      const rates = await apiGet<readonly FxRateView[]>('/admin/fx-rates');
      // Review W20: the server's push about our own remove can land while its Undo is on the way, and would draw the
      // row out and back. While a change of ours is in flight, what is on screen stands; the last answer brings the
      // server's whole list (`takeServerRates`).
      if (queryClient.isMutating({ mutationKey: FX_MUTATION_KEY }) > 0) return queryClient.getQueryData<readonly FxRateView[]>(FX_KEY) ?? rates;
      return rates;
    },
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

let pendingRateCount = 0;

/**
 * Adding, removing and Undo queue behind each other, so an Undo never reaches the server before the remove it undoes.
 * Each draws itself at once; only the last to answer takes the server's list, or an earlier answer would draw a later
 * click back for a moment (§5a rule 3).
 */
const FX_MUTATION_KEY = ['admin', 'fx-rates', 'change'] as const;
const FX_SCOPE = { id: 'admin-fx-rates' } as const;

function takeServerRates(queryClient: ReturnType<typeof useQueryClient>, rates: readonly FxRateView[]): void {
  if (queryClient.isMutating({ mutationKey: FX_MUTATION_KEY }) <= 1) queryClient.setQueryData(FX_KEY, rates);
}

/**
 * Adds a rate. It is in the list, the chart and the pair at once (§5a rule 1) under a stand-in id, and swapped for the
 * server's list when it answers; a refusal takes it out again and the form keeps what was typed.
 */
export function useAddRate() {
  const queryClient = useQueryClient();
  return useMutation<readonly FxRateView[], ApiError, FxRateInput & { readonly createdByName: string | null }, { readonly pendingId: string }>({
    mutationKey: FX_MUTATION_KEY,
    scope: FX_SCOPE,
    mutationFn: ({ fromCurrency, toCurrency, rate, effectiveFrom }) => apiPost<readonly FxRateView[]>('/admin/fx-rates', { fromCurrency, toCurrency, rate, effectiveFrom }),
    onMutate: async ({ fromCurrency, toCurrency, rate, effectiveFrom, createdByName }) => {
      await queryClient.cancelQueries({ queryKey: FX_KEY });
      pendingRateCount += 1;
      const pendingId = `pending-rate-${pendingRateCount}`;
      const previous = queryClient.getQueryData<readonly FxRateView[]>(FX_KEY) ?? [];
      queryClient.setQueryData<readonly FxRateView[]>(FX_KEY, [...previous, { id: pendingId, fromCurrency, toCurrency, rate, effectiveFrom, createdByName }]);
      return { pendingId };
    },
    onError: (_error, _input, context) => {
      const current = queryClient.getQueryData<readonly FxRateView[]>(FX_KEY);
      if (current && context) queryClient.setQueryData(FX_KEY, current.filter((row) => row.id !== context.pendingId));
    },
    onSuccess: (rates) => takeServerRates(queryClient, rates),
  });
}

/** Whether a row is still on its way to the server, so it can't be removed yet. */
export function isPendingRate(rate: FxRateView): boolean {
  return rate.id.startsWith('pending-rate-');
}

/** Remove's Undo: the same rate back, at once, and out again with the reason if its day has been taken since. */
export function useRestoreRate() {
  const queryClient = useQueryClient();
  return useMutation<readonly FxRateView[], ApiError, FxRateView>({
    mutationKey: FX_MUTATION_KEY,
    scope: FX_SCOPE,
    mutationFn: (rate) => apiPost<readonly FxRateView[]>(`/admin/fx-rates/${rate.id}/restore`, {}),
    onMutate: async (rate) => {
      await queryClient.cancelQueries({ queryKey: FX_KEY });
      const current = queryClient.getQueryData<readonly FxRateView[]>(FX_KEY) ?? [];
      if (!current.some((row) => row.id === rate.id)) queryClient.setQueryData<readonly FxRateView[]>(FX_KEY, [...current, rate]);
    },
    onError: (_error, rate) => {
      const current = queryClient.getQueryData<readonly FxRateView[]>(FX_KEY);
      if (current) queryClient.setQueryData(FX_KEY, current.filter((row) => row.id !== rate.id));
    },
    onSuccess: (rates) => takeServerRates(queryClient, rates),
  });
}

/** Removes a rate at once (soft on the server). The page offers Undo, which is `useRestoreRate`. */
export function useRemoveRate() {
  const queryClient = useQueryClient();
  return useMutation<readonly FxRateView[], ApiError, FxRateView>({
    mutationKey: FX_MUTATION_KEY,
    scope: FX_SCOPE,
    mutationFn: (rate) => apiDelete<readonly FxRateView[]>(`/admin/fx-rates/${rate.id}`),
    onMutate: async (rate) => {
      await queryClient.cancelQueries({ queryKey: FX_KEY });
      const current = queryClient.getQueryData<readonly FxRateView[]>(FX_KEY);
      if (current) queryClient.setQueryData(FX_KEY, current.filter((row) => row.id !== rate.id));
    },
    onError: (_error, rate) => {
      // Put back this one rate: anything added since stays.
      const current = queryClient.getQueryData<readonly FxRateView[]>(FX_KEY) ?? [];
      if (!current.some((row) => row.id === rate.id)) queryClient.setQueryData<readonly FxRateView[]>(FX_KEY, [...current, rate]);
    },
    onSuccess: (rates) => takeServerRates(queryClient, rates),
  });
}

// --- ADMIN-13 ------------------------------------------------------------------------------

/** The active fields of a module, for a form. */
export function useCustomFields(module: CustomFieldModule): UseQueryResult<readonly CustomFieldView[], ApiError> {
  return useQuery<readonly CustomFieldView[], ApiError>({
    queryKey: fieldsKey(module),
    queryFn: () => apiGet<readonly CustomFieldView[]>(`/custom-fields?module=${module}`),
    staleTime: 60_000,
  });
}

export function useCustomValues(module: CustomFieldModule, entityId: string): UseQueryResult<readonly CustomFieldValueView[], ApiError> {
  return useQuery<readonly CustomFieldValueView[], ApiError>({
    queryKey: valuesKey(module, entityId),
    queryFn: () => apiGet<readonly CustomFieldValueView[]>(`/custom-field-values/${module}/${entityId}`),
    placeholderData: (previous) => previous,
  });
}

export function useSaveCustomValues(module: CustomFieldModule) {
  const queryClient = useQueryClient();
  return useMutation<readonly CustomFieldValueView[], ApiError, { readonly entityId: string; readonly input: SaveCustomValuesInput }, { readonly previous: readonly CustomFieldValueView[] | undefined }>({
    mutationFn: ({ entityId, input }) => apiPut<readonly CustomFieldValueView[]>(`/custom-field-values/${module}/${entityId}`, input),
    onMutate: async ({ entityId, input }) => {
      const key = valuesKey(module, entityId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<readonly CustomFieldValueView[]>(key);
      if (previous) {
        queryClient.setQueryData(
          key,
          previous.map((row) => (Object.hasOwn(input.values, row.field.id) ? { ...row, value: input.values[row.field.id] ?? null } : row)),
        );
      }
      return { previous };
    },
    onError: (_error, { entityId }, context) => {
      if (context?.previous) queryClient.setQueryData(valuesKey(module, entityId), context.previous);
    },
    onSettled: async (_data, _error, { entityId }) => {
      await queryClient.invalidateQueries({ queryKey: valuesKey(module, entityId) });
    },
  });
}

// --- ADMIN-14 ------------------------------------------------------------------------------

export function useMetricDictionary(): UseQueryResult<MetricDictionaryView, ApiError> {
  return useQuery<MetricDictionaryView, ApiError>({
    queryKey: DICTIONARY_KEY,
    queryFn: () => apiGet<MetricDictionaryView>('/metric-dictionary'),
    placeholderData: (previous) => previous,
  });
}

export function usePublishDictionary() {
  const queryClient = useQueryClient();
  return useMutation<MetricDictionaryView, ApiError, PublishDictionaryInput>({
    mutationFn: (input) => apiPost<MetricDictionaryView>('/admin/metric-dictionary', input),
    onSettled: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: DICTIONARY_KEY }), queryClient.invalidateQueries({ queryKey: APP_SETTINGS_KEY })]);
    },
  });
}
